// Clients → one client (Client Accounts plan, section 3, "Client"). The whole account on one page: who they are, every
// purchase on every channel and tender, what they sold or consigned to Starchive and what they were paid, spend by month,
// brands bought, sizes, notes and outreach. Every change (advisor, pinned tier, profile, a note, a merge) is signed with an
// owner ID and audited. Consent is read only here: it is ticked at the counter by the client's own answer (plan 5.6).
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Screen } from '../../ReportsApp'
import { readReport, reportsAction, useReport } from '../../api'
import { int, money } from '../../format'
import { AsyncButton, Badge, Button, Card, Tile } from '../../ui'
import { useOwnerSigner } from '../owner-edit'
import { AdvisorPick, type TopData } from './TopClients'
import { CHANNEL_LABEL, Dot, KIND_LABEL, Seg, SplitBar, SplitKey, TIER_LABEL, TierChip, type ClientRow, dateY, daysLabel, tenderLabel } from './clients-ui'

interface Line { title: string; brand: string; price_cents: number | null; image_url: string | null; product_id: string | null; qty?: number }
interface LedgerRow { kind: string; ref: string; at: string; label: string; amount: number; channel: string; tender: string; source: string; served_by: string | null; lines: Line[]; named: boolean }
interface NoteRow { id: string; kind: 'note' | 'outreach'; body: string; channel: string; outcome: string; by_employee: string; by_name: string; at: string }
interface Detail {
  client: ClientRow & { name_suggestion: string; name_suggestion_sure: string; pin_reason: string; pin_by: string | null; preferred_contact: string | null; sizes: Record<string, string>; sizes_auto?: Record<string, AutoSize>; old_notes: string; since: string | null
    consent_email_at: string | null; consent_email_by: string; consent_sms_at: string | null; consent_sms_by: string; shopify_cents: number }
  ledger: LedgerRow[]; notes: NoteRow[]; brands: { brand: string; spend: number; pct: number }[]; months: { month: string; spend: number }[]
  merged: { id: string; first_name: string; last_name: string; email: string; phone: string }[]
}
type LFilter = 'all' | 'purchase' | 'sold' | 'consigned' | 'payout' | 'trade'
const CONTACT: Record<string, string> = { text: 'Text', call: 'Call', email: 'Email', dm: 'Instagram DM' }
const NCHAN: Record<string, string> = { text: 'Text', call: 'Call', email: 'Email', dm: 'DM', in_person: 'In person' }
const SIZE_KEYS: [string, string][] = [['tops', 'Tops'], ['bottoms', 'Trousers'], ['belts', 'Belts'], ['shoes', 'Shoes'], ['rings', 'Rings'], ['other', 'Other']]
// a size worked out from what the client bought (0057, nightly); shown only where nobody has typed one
interface AutoSize { value: string; n: number; of: number; also: string; also_n: number; sure: 'likely' | 'maybe'; last: string | null }
const autoNote = (a: AutoSize) => (a.of === 1 ? 'from 1 buy' : `${a.n} of ${a.of} buys`) + (a.also ? `, also ${a.also}` : '')
const source = (by: string) => !by ? 'never asked' : by === 'shopify' ? 'from Shopify' : `ticked by ${by}`

function MonthBars({ months }: { months: { month: string; spend: number }[] }) {
  const max = Math.max(1, ...months.map((m) => m.spend))
  const MON = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']
  return (
    <div className="cl-months" role="img" aria-label={`Spend by month: ${months.filter((m) => m.spend).map((m) => `${m.month} ${money(m.spend, true)}`).join(', ') || 'nothing in 24 months'}`}>
      {months.map((m) => (
        <span key={m.month} className="cl-months__col" title={`${m.month}: ${money(m.spend, true)}`}>
          <i style={{ height: `${m.spend ? Math.max(3, (100 * m.spend) / max) : 0}%` }} className={m.spend ? '' : 'is-zero'} />
          <small>{MON[Number(m.month.slice(5)) - 1]}</small>
        </span>
      ))}
    </div>
  )
}

export function ClientDetail() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { data: d, error, loading, refresh } = useReport<Detail>('client', { id }, [id])
  const top = useReport<TopData>('clients_top')
  const signer = useOwnerSigner()
  const [lf, setLf] = useState<LFilter>('all')
  const [msg, setMsg] = useState('')
  const [panel, setPanel] = useState<'' | 'pin' | 'merge' | 'note' | 'outreach' | 'profile'>('')
  const c = d?.client
  const run = (action: string, body: Record<string, unknown>, after?: () => void) => new Promise<void>((resolve, reject) => signer.signed(async () => {
    setMsg('')
    try { await reportsAction(action, { employee_id: signer.idRef.current, customer_id: id, ...body }); refresh(); top.refresh(); after?.(); resolve() } catch (e) { setMsg((e as Error).message); reject(e) }
  }))
  const ledger = useMemo(() => (d?.ledger ?? []).filter((l) => lf === 'all' ? true : lf === 'sold' ? (l.kind === 'sold_to_us' || l.kind === 'traded_in') : l.kind === lf), [d, lf])
  const counts = useMemo(() => {
    const L = d?.ledger ?? []
    return { all: L.length, purchase: L.filter((l) => l.kind === 'purchase').length, sold: L.filter((l) => l.kind === 'sold_to_us' || l.kind === 'traded_in').length, consigned: L.filter((l) => l.kind === 'consigned').length, payout: L.filter((l) => l.kind === 'payout').length, trade: L.filter((l) => l.kind === 'trade').length }
  }, [d])
  if (error) return <Screen title="Client"><div className="rp-error">{error}</div><Link to="/clients">← Top clients</Link></Screen>
  return (
    <Screen title={c?.name || (loading ? '…' : c ? (c.email || c.phone || 'Client') : 'Client')} sub={c ? <span className="cl-crumb"><Link to="/clients">Top clients</Link> · {c.city || 'No city on file'}{c.since ? ` · Customer since ${dateY(c.since)}` : ''}</span> : <Link to="/clients">Top clients</Link>} wide
      right={c && <>
        <Button onClick={() => setPanel(panel === 'note' ? '' : 'note')}>Add a note</Button>
        <Button onClick={() => setPanel(panel === 'pin' ? '' : 'pin')}>{c.pinned ? 'Change pinned tier' : 'Pin tier'}</Button>
        <Button onClick={() => setPanel(panel === 'merge' ? '' : 'merge')}>Merge</Button>
        <Button primary onClick={() => setPanel(panel === 'outreach' ? '' : 'outreach')}>Log outreach</Button>
      </>}>
      {signer.askEl}
      {msg && <div className="rp-error">{msg}</div>}
      {c && (
        <div className="cl-head">
          <div className="cl-head__id">
            <TierChip tier={c.tier} pinned={c.pinned} />
            {c.seller && <Badge tone="green">Sells to Starchive</Badge>}
            {c.consignor && <Badge tone="grey">Consignor {c.consignor_key}</Badge>}
            {c.internal && <Badge tone="amber">Internal</Badge>}
            <span className="cl-head__adv">Advisor <AdvisorPick row={c} advisors={top.data?.advisors ?? []} onSaved={() => { refresh(); top.refresh() }} signer={signer} /></span>
          </div>
          <div className="cl-head__contact">
            {c.phone && <a href={`sms:${c.phone}`}>{c.phone}</a>}
            {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
            {c.instagram && <a href={`https://instagram.com/${c.instagram}`} target="_blank" rel="noreferrer">@{c.instagram}</a>}
            <span>Prefers {c.preferred_contact ? CONTACT[c.preferred_contact] : <i className="rp-muted">not recorded</i>}</span>
            <span title={c.consent_email_at ? `Since ${dateY(c.consent_email_at)}` : ''}><Dot on={c.email_ok} label="Email marketing" /> Email marketing {c.email_ok ? 'yes' : 'no'} <small className="rp-muted">({source(c.consent_email_by)})</small></span>
            <span><Dot on={c.sms_ok} label="SMS marketing" /> SMS {c.sms_ok ? 'yes' : 'no'} <small className="rp-muted">({source(c.consent_sms_by)})</small></span>
            <button type="button" className="rp-linkish" onClick={() => setPanel(panel === 'profile' ? '' : 'profile')}>Edit profile</button>
          </div>
          {c.pinned && <div className="cl-small rp-muted">Tier pinned to {TIER_LABEL[c.tier ?? '']}{c.pin_by ? ` by ${c.pin_by}` : ''}: {c.pin_reason}</div>}
        </div>
      )}
      {c && c.stub && <NameStub c={c} run={run} />}
      {c && panel === 'pin' && <PinPanel c={c} run={run} close={() => setPanel('')} />}
      {c && panel === 'merge' && <MergePanel c={c} run={run} close={() => setPanel('')} onMerged={(into) => navigate('/clients/c/' + into)} />}
      {c && (panel === 'note' || panel === 'outreach') && <NotePanel kind={panel} run={run} close={() => setPanel('')} />}
      {c && panel === 'profile' && <ProfilePanel c={c} run={run} close={() => setPanel('')} />}
      <div className="rp-grid rp-grid--tiles">
        <Tile label="Lifetime, every tender" value={c ? money(c.life, true) : '…'} sub={c ? `Shopify ${money(c.shopify_cents, true)} · counter ${money(c.counter, true)}` : ''} />
        <Tile label="Last 12 months" value={c ? money(c.l12, true) : '…'} sub={c ? `${int(c.purchases)} purchases in all` : ''} />
        <Tile label="Average purchase" value={c ? money(c.avg, true) : '…'} sub={c?.first ? `First ${dateY(c.first)}` : 'No purchase yet'} />
        <Tile label="Sold to Starchive" value={c ? money(c.sold, true) : '…'} sub={c ? (c.sold_n ? `${int(c.sold_n)} pieces` : c.consigned_n ? `${int(c.consigned_n)} consigned, ${money(c.paid_out, true)} paid out` : 'Nothing yet') : ''} />
        <Tile label="Since last purchase" tone={c?.lapsed ? 'red' : 'plain'} value={c ? daysLabel(c.days) : '…'} sub={c?.last ? `Last ${dateY(c.last)}` : ''} />
      </div>
      <div className="rp-grid rp-grid--2">
        <Card title="Ledger" note="Every channel and tender, newest first" right={c && <SplitBar split={c.split} height={8} />}>
          <div className="cl-lfilter">
            <Seg label="Ledger filter" value={lf} onChange={setLf} options={([['all', 'All'], ['purchase', 'Bought'], ['sold', 'Sold to us'], ['consigned', 'Consigned'], ['payout', 'Paid out'], ['trade', 'Trades']] as [LFilter, string][]).filter(([k]) => k === 'all' || counts[k]).map(([k, l]) => [k, `${l} ${counts[k]}`])} />
          </div>
          <div className="cl-ledger">
            {loading && !d && <div className="rp-empty">…</div>}
            {d && !ledger.length && <div className="rp-empty">Nothing on record yet</div>}
            {ledger.map((l) => {
              const first = l.lines[0]
              const more = l.lines.length - 1
              const out = l.kind === 'sold_to_us' || l.kind === 'traded_in' || l.kind === 'payout'
              return (
                <div key={l.kind + l.ref} className="cl-lrow">
                  {first?.image_url ? <img className="cl-lrow__pic" src={first.image_url} alt="" loading="lazy" /> : <span className="cl-lrow__pic cl-lrow__pic--none" aria-hidden="true" />}
                  <span className="cl-lrow__main">
                    <b>{first?.title || l.label}{more > 0 ? ` + ${more} more` : ''}</b>
                    <small>{dateY(l.at)} · {KIND_LABEL[l.kind] ?? l.kind} · {l.label}{l.tender ? ` · ${tenderLabel(l.tender)}` : ''}{l.channel && l.kind === 'purchase' ? ` · ${CHANNEL_LABEL[l.channel] ?? l.channel}` : ''}{l.served_by ? ` · ${l.served_by}` : ''}{l.named ? ' · buyer named by an owner' : ''}</small>
                  </span>
                  <span className={'cl-lrow__amt' + (out ? ' rp-tone-green' : '')}>{l.kind === 'consigned' ? (l.amount ? `min ${money(l.amount, true)}` : '') : (out ? '+' : '') + money(l.amount, true)}</span>
                </div>
              )
            })}
          </div>
          <div className="cl-tablehead"><SplitKey /></div>
        </Card>
        <div className="cl-side">
          <Card title="Spend by month" note="Last 24 months">{d ? <MonthBars months={d.months} /> : <div className="rp-empty">…</div>}</Card>
          <Card title="Taste" right={<button type="button" className="rp-linkish" onClick={() => setPanel('profile')}>Edit sizes</button>}>
            <div className="cl-taste">
              <div>
                <h3>Sizes</h3>
                {SIZE_KEYS.filter(([k]) => c?.sizes?.[k] || c?.sizes_auto?.[k]).map(([k, l]) => {
                  const typed = c?.sizes?.[k], a = c?.sizes_auto?.[k]
                  return typed
                    ? <div key={k} className="cl-kv"><span>{l}</span><b>{typed}</b></div>
                    : <div key={k} className="cl-kv" title={`Worked out from purchases (${a!.sure}). Edit sizes to confirm or correct it.`}><span>{l}</span><b className={a!.sure === 'likely' ? '' : 'rp-muted'}>{a!.value} <small className="rp-muted">{autoNote(a!)}</small></b></div>
                })}
                {c && SIZE_KEYS.some(([k]) => !c.sizes?.[k] && c.sizes_auto?.[k]) && <p className="rp-muted cl-small">Sizes with a buy count are from purchases. Confirm at the next visit.</p>}
                {c && !SIZE_KEYS.some(([k]) => c.sizes?.[k] || c.sizes_auto?.[k]) && <p className="rp-muted cl-small">None recorded. Ask at the next visit.</p>}
              </div>
              <div>
                <h3>Brands bought</h3>
                {(d?.brands ?? []).map((b) => <div key={b.brand} className="cl-brand"><span>{b.brand}</span><span className="rp-bar"><i style={{ width: `${b.pct}%` }} /></span><span className="rp-muted">{b.pct}%</span></div>)}
                {d && !d.brands.length && <p className="rp-muted cl-small">Nothing bought yet</p>}
              </div>
            </div>
          </Card>
          <Card title="Notes and outreach" right={<Button small onClick={() => setPanel('note')}>Add</Button>}>
            <div className="cl-notes">
              {(d?.notes ?? []).map((n) => (
                <div key={n.id} className="cl-note">
                  <small>{dateY(n.at)} · {n.by_name}{n.kind === 'outreach' ? ` · Outreach${n.channel ? ` by ${NCHAN[n.channel] ?? n.channel}` : ''}` : ''}</small>
                  <span>{n.body}{n.outcome && <em className="rp-tone-green"> · {n.outcome}</em>}</span>
                </div>
              ))}
              {c?.old_notes && <div className="cl-note"><small>Note on the customer record</small><span>{c.old_notes}</span></div>}
              {d && !d.notes.length && !c?.old_notes && <p className="rp-empty">No notes yet. A note goes on the card after every visit and every deal closed over text.</p>}
            </div>
          </Card>
          {!!d?.merged.length && <Card title="Merged into this client">{d.merged.map((m) => <div key={m.id} className="cl-note"><span>{`${m.first_name} ${m.last_name}`.trim() || 'No name'} · {m.email || m.phone}</span></div>)}</Card>}
        </div>
      </div>
    </Screen>
  )
}

type Run = (action: string, body: Record<string, unknown>, after?: () => void) => Promise<void>
function PinPanel({ c, run, close }: { c: Detail['client']; run: Run; close: () => void }) {
  const [tier, setTier] = useState<string>(c.pinned ? c.tier ?? 'top' : 'top')
  const [reason, setReason] = useState(c.pinned ? c.pin_reason : '')
  return (
    <div className="cl-panel">
      <b>Pin a tier</b><span className="rp-muted cl-small">Tiers come from the rule; a pin overrides it until removed. Say why.</span>
      <div className="cl-panel__row">
        <Seg label="Tier" value={tier} onChange={setTier} options={[['top', 'Top'], ['core', 'Core'], ['buyer', 'Buyer'], ['prospect', 'Prospect']]} />
        <input className="rp-input" placeholder="Why (e.g. buys through a friend's account)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <AsyncButton primary run={() => run('client_tier_pin', { tier, reason }, close)}>Pin</AsyncButton>
        {c.pinned && <AsyncButton run={() => run('client_tier_pin', { tier: null }, close)}>Remove pin</AsyncButton>}
        <Button onClick={close}>Cancel</Button>
      </div>
    </div>
  )
}

function NotePanel({ kind, run, close }: { kind: 'note' | 'outreach'; run: Run; close: () => void }) {
  const [body, setBody] = useState('')
  const [channel, setChannel] = useState(kind === 'outreach' ? 'text' : '')
  const [outcome, setOutcome] = useState('')
  return (
    <div className="cl-panel">
      <b>{kind === 'outreach' ? 'Log outreach' : 'Add a note'}</b>
      <span className="rp-muted cl-small">{kind === 'outreach' ? 'What was sent or said, and what came of it. Personal texts from the advisor are not marketing.' : 'Sizes, taste, who they came in with, what they are hunting for. Dated and signed.'}</span>
      {kind === 'outreach' && <Seg label="How" value={channel} onChange={setChannel} options={[['text', 'Text'], ['call', 'Call'], ['email', 'Email'], ['dm', 'DM'], ['in_person', 'In person']]} />}
      <textarea className="rp-input cl-textarea" rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder={kind === 'outreach' ? 'Sent 3 photos of the October drop' : 'Collects 2001 to 2004 Raf. Size 48 jackets.'} />
      <div className="cl-panel__row">
        {kind === 'outreach' && <input className="rp-input" placeholder="Outcome (optional): replied, bought, no answer" value={outcome} onChange={(e) => setOutcome(e.target.value)} />}
        <AsyncButton primary run={() => run('client_note', { kind, body, channel: kind === 'outreach' ? channel : '', outcome }, close)}>Save</AsyncButton>
        <Button onClick={close}>Cancel</Button>
      </div>
    </div>
  )
}

function ProfilePanel({ c, run, close }: { c: Detail['client']; run: Run; close: () => void }) {
  const [f, setF] = useState({ preferred_contact: c.preferred_contact ?? '', city: c.city ?? '', instagram: c.instagram ?? '', sizes: { ...(c.sizes ?? {}) } as Record<string, string> })
  return (
    <div className="cl-panel">
      <b>Profile</b><span className="rp-muted cl-small">Name, phone and email are changed at the counter so Shopify stays in step. Consent is ticked there too, by the client's own answer.</span>
      <div className="cl-panel__grid">
        <label className="rp-field"><span className="rp-field__label">Prefers</span>
          <select className="rp-select" value={f.preferred_contact} onChange={(e) => setF({ ...f, preferred_contact: e.target.value })}><option value="">Not recorded</option>{Object.entries(CONTACT).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <label className="rp-field"><span className="rp-field__label">City</span><input className="rp-input" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></label>
        <label className="rp-field"><span className="rp-field__label">Instagram</span><input className="rp-input" value={f.instagram} onChange={(e) => setF({ ...f, instagram: e.target.value })} placeholder="handle" /></label>
        {SIZE_KEYS.map(([k, l]) => <label key={k} className="rp-field"><span className="rp-field__label">{l}</span><input className="rp-input" value={f.sizes[k] ?? ''} placeholder={c.sizes_auto?.[k] ? `${c.sizes_auto[k].value} (from purchases)` : ''} onChange={(e) => setF({ ...f, sizes: { ...f.sizes, [k]: e.target.value } })} /></label>)}
      </div>
      <div className="cl-panel__row">
        <AsyncButton primary run={() => run('client_profile', { preferred_contact: f.preferred_contact || null, city: f.city, instagram: f.instagram, sizes: f.sizes }, close)}>Save</AsyncButton>
        <Button onClick={close}>Cancel</Button>
      </div>
    </div>
  )
}

export function ClientSearch({ onPick, exclude }: { onPick: (c: ClientRow) => void; exclude?: string }) {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState<ClientRow[]>([])
  const [busy, setBusy] = useState(false)
  const search = async (v: string) => {
    setQ(v)
    if (v.trim().length < 2) { setRows([]); return }
    setBusy(true)
    try { const r = await readReport<{ rows: ClientRow[] }>('clients_search', { q: v }); setRows(r.rows.filter((x) => x.id !== exclude)) } finally { setBusy(false) }
  }
  return (
    <div className="cl-search">
      <input className="rp-input" autoFocus placeholder="Name, email or phone" value={q} onChange={(e) => void search(e.target.value)} aria-label="Find a client" />
      {busy && <span className="rp-muted cl-small">…</span>}
      {rows.length > 0 && <div className="cl-search__list">{rows.map((r) => (
        <button key={r.id} type="button" onClick={() => { onPick(r); setRows([]); setQ(r.name) }}>
          <b>{r.name}</b><small>{[r.email, r.phone, r.city].filter(Boolean).join(' · ')} · {money(r.life, true)}</small>
        </button>))}</div>}
    </div>
  )
}

function MergePanel({ c, run, close, onMerged }: { c: Detail['client']; run: Run; close: () => void; onMerged: (into: string) => void }) {
  const [into, setInto] = useState<ClientRow | null>(null)
  const [reason, setReason] = useState('')
  return (
    <div className="cl-panel">
      <b>Merge {c.name} into another client</b>
      <span className="rp-muted cl-small">For the same person on two records. This record's sales, orders and notes move to the one you pick; nothing is deleted. Shopify keeps both profiles until they are merged there too.</span>
      <ClientSearch onPick={setInto} exclude={c.id} />
      {into && <div className="cl-panel__row">
        <span>Into <b>{into.name}</b> ({money(into.life, true)}, {into.email || into.phone})</span>
        <input className="rp-input" placeholder="Why they are the same person" value={reason} onChange={(e) => setReason(e.target.value)} />
        <AsyncButton primary run={() => run('client_merge', { from_id: c.id, into_id: into.id, reason }, () => onMerged(into.id))}>Merge</AsyncButton>
      </div>}
      <div><Button onClick={close}>Cancel</Button></div>
    </div>
  )
}

/** A customer who bought and left no name (Who is this): name them here, with the guess from the email when there is one. */
function NameStub({ c, run }: { c: Detail['client']; run: Run }) {
  const usable = !!c.name_suggestion && !c.name_suggestion.includes('unknown') && !c.name_suggestion.startsWith('?') && !/^[A-Z]\. /.test(c.name_suggestion)
  const [v, setV] = useState(usable ? c.name_suggestion : '')
  return (
    <div className="cl-panel">
      <b>Who is this?</b>
      <span className="rp-muted cl-small">They bought {money(c.life, true)} but never left a name, so they are not on the client lists yet. {usable ? `Our guess from the email: ${c.name_suggestion}.` : c.name_suggestion ? `Hint from the email: ${c.name_suggestion.replace(' (last name unknown)', '…')}.` : ''} The purchases below may jog a memory.</span>
      <div className="cl-panel__row">
        <input className="rp-input" value={v} onChange={(e) => setV(e.target.value)} placeholder="First and last name" aria-label="Name" />
        <AsyncButton primary disabled={!v.trim()} run={() => { const p = v.trim().replace(/\s+/g, ' ').split(' '); return run('client_name', { first_name: p[0], last_name: p.slice(1).join(' '), from_suggestion: v.trim() === c.name_suggestion }) }}>Save the name</AsyncButton>
      </div>
    </div>
  )
}
